import React from 'react';
import { Phone, PhoneIncoming, PhoneOff, Clock, CheckCircle, TrendingUp } from 'lucide-react';
import { useDailyCallCenterHealthSummary } from '../hooks/useReports';
import KPICard from '../components/KPICard';

export default function CallCenterDashboard() {
  const { data, isLoading } = useDailyCallCenterHealthSummary();

  // Calculate summary KPIs from the data
  const summaryKPIs = React.useMemo(() => {
    if (!data?.data || data.data.length === 0) {
      return {
        totalInteractions: 0,
        answeredCalls: 0,
        abandonedCalls: 0,
        avgQueueTime: 0,
        avgHandleTime: 0,
        avgSLA: 0,
      };
    }

    const totals = data.data.reduce(
      (acc, row) => ({
        totalInteractions: acc.totalInteractions + (parseInt(row.total_interactions) || 0),
        answeredCalls: acc.answeredCalls + (parseInt(row.answered_calls) || 0),
        abandonedCalls: acc.abandonedCalls + (parseInt(row.abandoned_calls) || 0),
        queueTimeSum: acc.queueTimeSum + (parseFloat(row.avg_queue_time_sec) || 0),
        handleTimeSum: acc.handleTimeSum + (parseFloat(row.avg_handle_time_sec) || 0),
        slaSum: acc.slaSum + (parseFloat(row.sla_30s_pct) || 0),
        count: acc.count + 1,
      }),
      { totalInteractions: 0, answeredCalls: 0, abandonedCalls: 0, queueTimeSum: 0, handleTimeSum: 0, slaSum: 0, count: 0 }
    );

    return {
      totalInteractions: totals.totalInteractions,
      answeredCalls: totals.answeredCalls,
      abandonedCalls: totals.abandonedCalls,
      avgQueueTime: totals.count > 0 ? (totals.queueTimeSum / totals.count).toFixed(1) : 0,
      avgHandleTime: totals.count > 0 ? (totals.handleTimeSum / totals.count).toFixed(1) : 0,
      avgSLA: totals.count > 0 ? (totals.slaSum / totals.count).toFixed(1) : 0,
    };
  }, [data]);

  const kpis = [
    {
      title: 'Total Interactions',
      value: summaryKPIs.totalInteractions,
      icon: Phone,
      format: 'number',
    },
    {
      title: 'Answered Calls',
      value: summaryKPIs.answeredCalls,
      icon: PhoneIncoming,
      format: 'number',
    },
    {
      title: 'Abandoned Calls',
      value: summaryKPIs.abandonedCalls,
      icon: PhoneOff,
      format: 'number',
    },
    {
      title: 'Avg Queue Time (sec)',
      value: summaryKPIs.avgQueueTime,
      icon: Clock,
      format: 'number',
    },
    {
      title: 'Avg Handle Time (sec)',
      value: summaryKPIs.avgHandleTime,
      icon: Clock,
      format: 'number',
    },
    {
      title: 'Avg SLA 30s %',
      value: summaryKPIs.avgSLA,
      icon: CheckCircle,
      format: 'percent',
    },
  ];

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Call Center Metrics</h2>
        <p className="text-gray-500 mt-1">
          Overview of call center performance and health metrics
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6">
        {kpis.map((kpi, index) => (
          <KPICard key={index} {...kpi} loading={isLoading} />
        ))}
      </div>

      <div className="card">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="w-5 h-5 text-primary-600" />
          <h3 className="text-lg font-semibold text-gray-900">Quick Links</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="p-4 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg">
            <h4 className="font-medium text-amber-900">Daily Health Summary</h4>
            <p className="text-sm text-amber-700 mt-1">
              View detailed daily call center metrics including SLA compliance and call volumes.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
